import * as React from "react";
import { Video } from "./Video.js";
import { Img } from "./Img.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import {
  MONTAGE_ALT,
  MONTAGE_DESKTOP,
  MONTAGE_MOBILE,
  MONTAGE_POSTER,
  montageUrl,
} from "../lib/montage.js";
import "./MontageVideo.css";

/**
 * Mirrors the CSS-only `prefers-reduced-motion` treatment used across the app,
 * but in JS — CSS can't stop a `<video autoplay>`, so we swap the whole element
 * for the poster when the user asks for reduced motion. Returns `false` on the
 * server / first render so it agrees with the prerendered markup, then flips.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);
  return reduced;
}

export interface MontageVideoProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * The marketing gameplay reel as a muted, looping, lazy autoplay video in a
 * 16:9 card. Reuses the CDN-backed <Video> component (TEX-1438): it stays
 * `preload="none"` until the card scrolls into view, autoplays muted, loops, and
 * pauses when offscreen. Picks the mobile vs desktop derivative by viewport, and
 * degrades to the poster image when the user prefers reduced motion.
 *
 * Deliberately non-priority (lazy) on every surface: the download defers until
 * the card is near the viewport, by which point the viewport pick has settled —
 * so the desktop file is never fetched on a phone, and LCP isn't taxed.
 */
export function MontageVideo({ className, style }: MontageVideoProps): React.ReactElement {
  const isMobile = useIsMobile();
  const reducedMotion = usePrefersReducedMotion();
  const file = isMobile ? MONTAGE_MOBILE : MONTAGE_DESKTOP;

  return (
    <div className={`montage-card${className ? ` ${className}` : ""}`} style={style}>
      {reducedMotion ? (
        <Img src={MONTAGE_POSTER} alt={MONTAGE_ALT} intent="hero" className="montage-card__media" />
      ) : (
        <Video
          src={montageUrl(file)}
          poster={MONTAGE_POSTER}
          alt={MONTAGE_ALT}
          loop
          muted
          priority={false}
          className="montage-card__media"
        />
      )}
    </div>
  );
}
